import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useTaskActions } from '../api';
import { NoteField, type NoteFieldHandle } from './NoteField';
import { FitInput } from './FitInput';
import { useShelf } from '../../stock-manager/lib/useStock';

/**
 * A centred modal over the WHOLE screen — rail, bar and pane under one scrim,
 * as the canvases draw it. The kit's Modal positions against the nearest
 * `.maie`, which on a routed page is the content column only.
 */
export function NurseModal({
  open,
  onClose,
  title,
  sub,
  children,
  foot,
  width = 520,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
  foot?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="maie desk kit-modal-dock" style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div className="dscrim" onClick={onClose} role="presentation" />
      <div
        className="dmodal dmodal-phone"
        style={{ width: `min(${width}px, calc(100vw - 32px))`, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <p className="t-title">{title}</p>
        {sub ? <p className="t-sec mut">{sub}</p> : null}
        {children}
        {foot ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>{foot}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** A quoted, editable line in a ghost card — the canvas's note, with its pencil. */
function NoteCard({ heading, value, onChange, placeholder, maxLength = 200 }: {
  heading: ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  maxLength?: number;
}) {
  const ref = useRef<NoteFieldHandle>(null);
  return (
    <div className="card card-ghost mt12" style={{ cursor: 'text' }}
      onClick={(e) => {
        if (!(e.target as HTMLElement).closest('.nurse-note')) ref.current?.focus();
      }}>
      <p className="t-ovl mut">{heading}</p>
      <p className="t-sec sec mt4">
        <NoteField ref={ref} label={typeof heading === 'string' ? heading : 'Note'} value={value} onChange={onChange}
          maxLength={maxLength} placeholder={placeholder} />
      </p>
    </div>
  );
}

/**
 * FLAG LOW STOCK — `ison.flaglow`. She flags it; the holder adjusts it.
 *
 * The canvas lists the shelf's items with where each stands, plus one row for
 * anything untracked. The shelf list is not kept in MAIE yet (BACKEND-GAP —
 * the Stock manager lane builds it), so its rows say so and cannot be picked;
 * the untracked row works today: it goes as a line, not a count, to the
 * clinic's managers.
 */
export function FlagLowModal({
  open,
  onClose,
  initial = '',
}: {
  open: boolean;
  onClose: () => void;
  initial?: string;
}) {
  const { flagLow } = useTaskActions();
  const [line, setLine] = useState(initial);
  /* The shelf, low first — tapping a row names it; anything else is typed. */
  const { data: items = [] } = useShelf();
  const shelf = [...items].sort((a, b) => Number(b.low) - Number(a.low)).slice(0, 8);
  const untracked = !items.some((it) => it.name === line);
  useEffect(() => {
    if (open) setLine(initial);
  }, [open, initial]);

  const send = () => {
    if (!line.trim()) {
      toast.error('Write what is running out — an untracked thing goes as a line');
      return;
    }
    flagLow.mutate(line.trim(), {
      onSuccess: () => {
        toast.success('Flagged low — the clinic’s managers are told');
        onClose();
      },
      onError: (e) => toast.error((e as Error).message || 'Could not flag it'),
    });
  };

  return (
    <NurseModal
      open={open}
      onClose={onClose}
      title="Flag low stock"
      sub="You flag it; the holder adjusts it. Untracked things go the same way."
      width={560}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={send} disabled={flagLow.isPending}>
            Tell the Stock manager
          </button>
        </>
      }
    >
      <table className="dtable" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th style={{ width: '52%' }}>What is running out</th>
            <th style={{ width: '48%' }}>Where it stands</th>
          </tr>
        </thead>
        <tbody>
          {shelf.map((it) => (
            <tr key={it.id} className={line === it.name ? 'rowc sel' : 'rowc'} aria-selected={line === it.name}
              tabIndex={0} onClick={() => setLine(it.name)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLine(it.name); } }}>
              <td><span className="t-body">{it.name}</span></td>
              <td>
                <span className={it.low ? 't-sec amb' : 't-sec mut'}>
                  {it.inStock} {it.unit ?? 'left'}{it.minLevel !== null ? ` · min ${it.minLevel}` : ''}
                </span>
              </td>
            </tr>
          ))}
          <tr className={untracked ? 'rowc sel' : 'rowc'} aria-selected={untracked}>
            <td><span className="t-body">Something untracked — printer paper, coffee</span></td>
            <td><span className="t-sec mut">not on the shelf list · goes as a line, not a count</span></td>
          </tr>
        </tbody>
      </table>
      <NoteCard heading="Note (optional)" value={line} onChange={setLine}
        placeholder="what is running out, in your words" />
    </NurseModal>
  );
}

const TIMER_WHY: Array<[number, string]> = [
  [15, 'shorter — the drops took early'],
  [20, 'the usual dilation time'],
  [30, 'slower to dilate — a dark iris, a child'],
  [45, 'the room is cold · a second round of drops'],
];

/**
 * CHANGE THE TIMER — `ison.timer`. The timer is hers to set, and every change
 * is audited on the task (who · from → to, and her line for a typed one).
 */
export function TimerModal({
  open,
  onClose,
  current,
  defaultSeconds,
  flowName,
  busy,
  onSet,
}: {
  open: boolean;
  onClose: () => void;
  current: number | null;
  defaultSeconds: number | null;
  flowName: string | null;
  busy?: boolean;
  onSet: (seconds: number, note?: string) => void;
}) {
  const defMin = defaultSeconds ? Math.round(defaultSeconds / 60) : null;
  const rows = TIMER_WHY.map(([m, why]) => [m, m === defMin
    ? `the ${flowName ? 'flow' : 'order'}’s default${flowName ? ` · ${flowName}` : ''}` : why] as [number, string]);
  if (defMin && !rows.some(([m]) => m === defMin)) {
    rows.push([defMin, `the ${flowName ? 'flow' : 'order'}’s default${flowName ? ` · ${flowName}` : ''}`]);
    rows.sort((a, b) => a[0] - b[0]);
  }
  const curMin = current ? Math.round(current / 60) : null;
  const [picked, setPicked] = useState<number | 'other' | null>(null);
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!open) return;
    setPicked(curMin && rows.some(([m]) => m === curMin) ? curMin : curMin ? 'other' : null);
    setMinutes(curMin && !rows.some(([m]) => m === curMin) ? String(curMin) : '');
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = () => {
    if (picked === null) {
      toast.error('Pick a timer first');
      return;
    }
    if (picked === 'other') {
      const m = Number(minutes.trim());
      if (!Number.isInteger(m) || m < 1 || m > 240) {
        toast.error('Type the minutes — a whole number, 1 to 240');
        return;
      }
      if (!note.trim()) {
        toast.error('A typed timer needs your line on the record');
        return;
      }
      onSet(m * 60, note.trim());
      return;
    }
    onSet(picked * 60);
  };

  const row = (key: number | 'other', label: ReactNode, why: string) => (
    <tr key={key} className={picked === key ? 'rowc sel' : 'rowc'} tabIndex={0} aria-selected={picked === key}
      onClick={() => setPicked(key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setPicked(key);
        }
      }}>
      <td><span className="t-body">{label}</span></td>
      <td><span className="t-sec mut">{why}</span></td>
    </tr>
  );

  return (
    <NurseModal
      open={open}
      onClose={onClose}
      title="Change the timer"
      sub={
        <>
          The timer is yours to set. The change is audited — who · from → to — and the clock follows the actual
          timer, not the default.
        </>
      }
      width={560}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={set} disabled={busy}>Set the timer</button>
        </>
      }
    >
      <table className="dtable" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th style={{ width: '38%' }}>Timer</th>
            <th style={{ width: '62%' }}>Why you would</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([m, why]) => row(m, `${m} min`, why))}
          {row('other', picked === 'other' ? (
            <>
              <FitInput aria-label="Minutes" value={minutes} placeholder="type the minutes" inputMode="numeric"
                maxLength={3} autoFocus onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ''))}
                onClick={(e) => e.stopPropagation()} />
              {minutes ? ' min' : null}
            </>
          ) : 'Other — type the minutes', 'any timer, with your line on the record')}
        </tbody>
      </table>
      {picked === 'other' ? (
        <NoteCard heading="Your line" value={note} onChange={setNote} placeholder="why this timer — it is the record" />
      ) : null}
    </NurseModal>
  );
}

/**
 * REOPEN — SAY WHY. A closed task goes back on the list; the reason is
 * required, and the log keeps both lines (what closed it, and why it reopened).
 */
export function ReopenModal({
  open,
  onClose,
  title,
  busy,
  onReopen,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  busy?: boolean;
  onReopen: (reason: string) => void;
}) {
  const [why, setWhy] = useState('');
  useEffect(() => {
    if (open) setWhy('');
  }, [open]);
  const go = () => {
    if (!why.trim()) {
      toast.error('Say why — reopening keeps your reason on the record');
      return;
    }
    onReopen(why.trim());
  };
  return (
    <NurseModal
      open={open}
      onClose={onClose}
      title={`Reopen — ${title}`}
      sub="Reopening asks for a reason — the log keeps both lines: what closed it, and why it is open again."
      width={560}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={go} disabled={busy}>Reopen</button>
        </>
      }
    >
      <NoteCard heading="Why (required)" value={why} onChange={setWhy} placeholder="the reason, in your words"
        maxLength={300} />
    </NurseModal>
  );
}
